import Loader from '../../Loader';
import {
  LoaderContainer,
  NoResultContainer
} from '../styles.maximisedpnawidget';

const fmtPrice = v =>
  v === null || v === undefined ? '—' : `$${Number(v).toLocaleString()}`;

// Reformat the YYYY-MM-DD string directly. Avoid `new Date(iso)`: it parses a
// date-only string as UTC midnight, then the local-time getters roll it back a
// day for any viewer west of UTC, mislabeling the whole matrix.
const fmtDate = iso => {
  if (!iso) return 'Flexible';
  const [y, m, d] = iso.split('-');
  return d ? `${+m}/${+d}/${y}` : iso;
};

const cellStyle = {
  border: '1px solid rgba(196, 196, 196, 0.5)',
  padding: '8px 12px',
  fontSize: '12px',
  textAlign: 'center',
  whiteSpace: 'nowrap'
};

const headStyle = {
  ...cellStyle,
  fontWeight: 600,
  // Opaque so scrolled rows don't show through the sticky header.
  background: '#f4f4f4',
  position: 'sticky',
  top: 0,
  zIndex: 1
};

const RentMatrixView = ({ unit, matrix, loading, error, onBack }) => {
  const pricing = matrix?.pricing || [];

  const terms =
    matrix?.leaseTerms && matrix.leaseTerms.length
      ? matrix.leaseTerms
      : [...new Set(pricing.map(p => p.leaseTerm).filter(t => t != null))].sort(
          (a, b) => a - b
        );

  // Unique lease-start dates, sorted ascending with the flexible (null) row last.
  const startDates = [...new Set(pricing.map(p => p.leaseStartDate ?? null))];
  startDates.sort((a, b) => {
    if (a === b) return 0;
    if (a === null) return 1;
    if (b === null) return -1;
    return new Date(a) - new Date(b);
  });

  const byKey = new Map(
    pricing.map(p => [`${p.leaseStartDate ?? ''}|${p.leaseTerm ?? ''}`, p.rent])
  );

  let body;
  if (loading) {
    body = (
      <LoaderContainer>
        <Loader />
      </LoaderContainer>
    );
  } else if (error) {
    body = <NoResultContainer>Pricing unavailable</NoResultContainer>;
  } else if (!pricing.length || !terms.length) {
    body = (
      <NoResultContainer>No lease-term pricing for this unit</NoResultContainer>
    );
  } else {
    body = (
      <div style={{ overflow: 'auto', maxHeight: '380px', padding: '0 10px' }}>
        <table style={{ borderCollapse: 'collapse', width: '100%' }}>
          <thead>
            <tr>
              <th style={headStyle}>Move-in</th>
              {terms.map(t => (
                <th key={t} style={headStyle}>
                  {t} months
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {startDates.map(sd => (
              <tr key={sd ?? 'flexible'}>
                <td style={{ ...cellStyle, fontWeight: 500 }}>{fmtDate(sd)}</td>
                {terms.map(t => (
                  <td key={t} style={cellStyle}>
                    {fmtPrice(byKey.get(`${sd ?? ''}|${t}`))}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          padding: '12px 10px'
        }}
      >
        <button
          type="button"
          onClick={onBack}
          style={{
            cursor: 'pointer',
            border: '1px solid rgba(196, 196, 196, 0.7)',
            background: 'white',
            borderRadius: '4px',
            padding: '4px 10px',
            fontSize: '12px'
          }}
        >
          ← Back to units
        </button>
        <span style={{ fontWeight: 600, fontSize: '14px' }}>
          {unit?.unitName
            ? `Pricing — Unit ${unit.displayUnitName}`
            : 'Pricing'}
        </span>
        {unit && (
          <span style={{ color: '#777', fontSize: '12px' }}>
            {unit.bedRooms ?? '—'} bed · {unit.bathRooms ?? '—'} bath
            {unit.sqft ? ` · ${unit.sqft} sqft` : ''}
          </span>
        )}
      </div>
      {body}
    </div>
  );
};

export default RentMatrixView;
